export const LANGUAGE_CODES = ["en", "nl", "th"] as const;
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

export interface SpeakerVariant {
  speaker: "masculine" | "feminine";
  text: string;
  usage: string;
}

export interface LocalizedForm {
  text: string;
  pronunciation?: string;
  note?: string;
  speakerVariants?: SpeakerVariant[];
}

export type LocalizedForms = Record<LanguageCode, LocalizedForm>;

export interface ReviewRecord {
  status: "machine-reviewed" | "human-reviewed";
  reviewedBy: string;
  reviewedAt: string;
  source: string;
}

export type LanguageReviews = Record<LanguageCode | "romanization", ReviewRecord>;

export interface VocabularyItem {
  id: string;
  meaningId: string;
  priority: number;
  forms: LocalizedForms;
  reviews: LanguageReviews;
}

export interface SentenceSegment {
  id: string;
  forms: LocalizedForms;
  pronunciation?: string;
  vocabularyItemId?: string;
}

export interface Sentence {
  id: string;
  meaningId: string;
  priority: number;
  forms: LocalizedForms;
  highlightedVocabularyIds: string[];
  breakdown?: SentenceSegment[];
  reviews: LanguageReviews;
}

export interface LessonTitle {
  en: string;
  nl: string;
  th: string;
}

export interface Lesson {
  id: string;
  day: number;
  slug: string;
  emoji: string;
  title: LessonTitle;
  summary: string;
  publicationStatus: "preview" | "published";
  provenance: string;
  vocabulary: VocabularyItem[];
  sentences: Sentence[];
}

export interface CurriculumLessonSummary {
  id: string;
  day: number;
  slug: string;
  emoji: string;
  title: LessonTitle;
  publicationStatus: Lesson["publicationStatus"];
  vocabularyCount: number;
  sentenceCount: number;
}

export interface CurriculumManifest {
  id: "starter-30";
  version: string;
  title: string;
  supportedLanguages: LanguageCode[];
  publishedAt: string;
  lessons: CurriculumLessonSummary[];
}

/**
 * One block of article prose. An article body is a list of these rather than a
 * string of markup, so the same content can be rendered to the page, walked for
 * a reading time, and checked for dead internal links without ever parsing HTML.
 */
export type ArticleBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "heading"; text: string }
  | { kind: "subheading"; text: string }
  | { kind: "list"; ordered?: boolean; items: string[] }
  | { kind: "table"; caption?: string; columns: string[]; rows: string[][] }
  | { kind: "callout"; title: string; text: string };

/**
 * A question a reader actually asks, with an answer that still means the same
 * thing after being lifted out of the page. The same pair renders on the page
 * and becomes an addressable question in the article's structured data.
 */
export interface ArticleFaq {
  question: string;
  answer: string;
}

/** One authority document an article leans on, with the date it was last opened. */
export interface ArticleSource {
  id: string;
  authority: string;
  title: string;
  url: string;
  lastChecked: string;
  /** When the citation has to be opened again. The publish gate enforces it. */
  nextReview: string;
}

export interface Article {
  slug: string;
  title: string;
  /** The meta description, and the only sentence a result list may show. */
  description: string;
  /**
   * The definition-first opening. It answers the title before context, before
   * caveats, and before the product, so a quote of the opening alone is still
   * correct. It doubles as the card summary on the index.
   */
  lede: string;
  emoji: string;
  category: string;
  date: string;
  updated: string;
  primaryKeyword: string;
  secondaryKeywords: string[];
  tags: string[];
  ctaLabel: string;
  ctaHref: string;
  /** Slugs of the articles this one hands the reader on to. */
  related: string[];
  body: ArticleBlock[];
  faqs: ArticleFaq[];
  sources: ArticleSource[];
  /** A named byline becomes a Person in structured data; null uses the site. */
  author: string | null;
  noindex: boolean;
  publicationStatus: "preview" | "published";
  review: ReviewRecord;
}

/**
 * The whole configuration of the app: two languages side by side, how much
 * lands on one sheet, and when the daily rhythm started.
 */
export interface SheetSettings {
  /** Language in the left column. */
  left: LanguageCode;
  /** Language in the right column. */
  right: LanguageCode;
  /** Optional name above the left column, e.g. "Papa". */
  leftLabel: string;
  /** Optional name above the right column, e.g. "Nok". */
  rightLabel: string;
  words: number;
  sentences: number;
  showPronunciation: boolean;
  timezone: string;
  startDate: string;
}

export interface ProgressState {
  completedDays: number[];
}

export interface AccessState {
  unlocked: boolean;
  unlockedAt: string | null;
  /**
   * The address left on day 8, kept so the ask is never repeated and the same
   * person is never posted to the list twice.
   */
  email: string | null;
}

export interface PersistedAppState {
  schemaVersion: 2;
  settings: SheetSettings | null;
  progress: ProgressState;
  access: AccessState;
}

export interface ApiEnvelope<T> {
  data: T;
  meta: {
    version: string;
    previewContent: boolean;
  };
}
